import { Match, Schema } from 'effect';

import { dateValueSchema } from './queryDates';
import { type QueryClause, type QueryExpression, type QueryValue } from './queryParser';
import { type QueryField, type QueryOperator, querySchema } from './querySchema';

type ScalarOperator = Exclude<QueryOperator, 'IN' | 'NOT IN' | 'IS EMPTY' | 'IS NOT EMPTY'>;

export type QueryOperatorMap<Value, Filter> = Readonly<
  Partial<Record<ScalarOperator, (value: Value) => Filter>> &
    Partial<Record<'IN' | 'NOT IN', (values: readonly [Value, ...Value[]]) => Filter>> &
    Partial<Record<'IS EMPTY' | 'IS NOT EMPTY', () => Filter>>
>;

type FieldConfiguration<Field extends QueryField, Value, Filter> = Omit<Field, 'operators'> & {
  readonly operators: Pick<
    QueryOperatorMap<Value, Filter>,
    NonNullable<Field['operators']>[number]
  >;
};

export type QueryFieldConfiguration<Filter> =
  | FieldConfiguration<Extract<QueryField, { type: 'id' }>, number, Filter>
  | FieldConfiguration<Extract<QueryField, { type: 'number' }>, number, Filter>
  | FieldConfiguration<Extract<QueryField, { type: 'text' }>, string, Filter>
  | FieldConfiguration<Extract<QueryField, { type: 'choice' }>, string, Filter>
  | FieldConfiguration<Extract<QueryField, { type: 'boolean' }>, boolean, Filter>
  | FieldConfiguration<Extract<QueryField, { type: 'date' }>, typeof dateValueSchema.Type, Filter>;

const scalarInput = (value: QueryValue): unknown =>
  Match.value(value).pipe(
    Match.when({ kind: 'string' }, (scalar) => scalar.text),
    Match.when({ kind: 'date' }, (scalar) => scalar.text),
    Match.when({ kind: 'number' }, (scalar) => scalar.value),
    Match.when({ kind: 'boolean' }, (scalar) => scalar.value),
    Match.orElse(() => undefined),
  );

const requiredOperator = <A>(operator: A | undefined, condition: QueryClause): A => {
  if (operator === undefined)
    throw new Error(`Unconfigured query clause: ${condition.field.name} ${condition.op}`);
  return operator;
};

const compileValue = <Value, Filter>(
  condition: QueryClause,
  valueSchema: Schema.Codec<Value, unknown>,
  operators: QueryOperatorMap<Value, Filter>,
): Filter => {
  const op = condition.op;
  if (op === 'IS EMPTY' || op === 'IS NOT EMPTY')
    return requiredOperator(operators[op], condition)();
  if (op === 'IN' || op === 'NOT IN') {
    const values = Schema.decodeUnknownSync(Schema.NonEmptyArray(valueSchema))(
      condition.value.items.map(scalarInput),
    );
    return requiredOperator(operators[op], condition)(values);
  }
  const value = Schema.decodeUnknownSync(valueSchema)(scalarInput(condition.value));
  return requiredOperator(operators[op], condition)(value);
};

const compileField = <Filter>(
  field: QueryFieldConfiguration<Filter>,
  condition: QueryClause,
): Filter => {
  if (field.type === 'id' || field.type === 'number') {
    return compileValue(condition, field.valueSchema ?? Schema.Finite, field.operators);
  }
  if (field.type === 'choice')
    return compileValue(condition, Schema.Literals(field.values), field.operators);
  if (field.type === 'text') return compileValue(condition, Schema.String, field.operators);
  if (field.type === 'date') return compileValue(condition, dateValueSchema, field.operators);
  return compileValue(condition, Schema.Boolean, field.operators);
};

export const configureQuery = <Filter>(configuration: {
  readonly entity: string;
  readonly searchPlaceholder?: string;
  readonly defaultSimpleField?: string;
  readonly fields: readonly [QueryFieldConfiguration<Filter>, ...QueryFieldConfiguration<Filter>[]];
  readonly and: (left: Filter, right: Filter) => Filter;
  readonly or: (left: Filter, right: Filter) => Filter;
  readonly not: (filter: Filter) => Filter;
  readonly text: (input: string) => Filter | undefined;
}) => {
  const schema = Schema.decodeUnknownSync(querySchema)({
    entity: configuration.entity,
    ...(configuration.defaultSimpleField === undefined
      ? {}
      : { defaultSimpleField: configuration.defaultSimpleField }),
    ...(configuration.searchPlaceholder === undefined
      ? {}
      : { searchPlaceholder: configuration.searchPlaceholder }),
    fields: configuration.fields.map(({ operators, ...field }) => ({
      ...field,
      operators: Object.keys(operators),
    })),
  });

  const compileClause = (condition: QueryClause): Filter => {
    const field = configuration.fields.find(
      (candidate) =>
        candidate.name === condition.field.name && candidate.type === condition.field.type,
    );
    if (field === undefined)
      throw new Error(`Unconfigured query clause: ${condition.field.name} ${condition.op}`);
    return compileField(field, condition);
  };

  const compile = (expression: QueryExpression): Filter => {
    if (expression.type === 'search') {
      const filter = configuration.text(expression.text);
      if (filter === undefined)
        throw new Error('The configured text search did not produce a filter');
      return filter;
    }
    if (expression.type === 'clause') return compileClause(expression);
    if (expression.type === 'and')
      return configuration.and(compile(expression.left), compile(expression.right));
    if (expression.type === 'or')
      return configuration.or(compile(expression.left), compile(expression.right));
    return configuration.not(compile(expression.expr));
  };

  return { schema, compile, text: configuration.text };
};
